import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { ASSETS } from "@/i18n";
import { OdyneCredit } from "@/components/OdyneCredit";
import { COMPANY, displayLegalName, formatAddress } from "@/config/company";
import { revokeConsent } from "@/lib/consent";
import { Instagram, Linkedin, Facebook } from "lucide-react";

/**
 * Footer.
 *
 * Oltre alla navigazione assolve due obblighi:
 *  - art. 7 D.Lgs 70/2003: i dati identificativi del gestore devono essere
 *    "facilmente, direttamente e permanentemente accessibili";
 *  - Linee guida cookie del Garante: l'utente deve poter modificare in ogni
 *    momento la scelta espressa sul banner.
 *
 * Regola: non aggiungere qui link con href="#". Un collegamento che non porta
 * da nessuna parte, se etichettato "Privacy", è di per sé una non conformità.
 */
export const Footer = () => {
  const { t } = useLang();

  const socials = [
    { key: "instagram", Icon: Instagram, url: COMPANY.social.instagram },
    { key: "linkedin", Icon: Linkedin, url: COMPANY.social.linkedin },
    { key: "facebook", Icon: Facebook, url: COMPANY.social.facebook },
  ].filter((s) => Boolean(s.url));

  const address = formatAddress();

  return (
    <footer data-testid="site-footer" className="bg-[#FAFAFA] border-t border-black/5">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={ASSETS.logo} alt="AURA" className="h-12 w-12 rounded-full object-contain bg-white" style={{ mixBlendMode: "multiply" }} />
              <div>
                <div className="font-serif text-3xl tracking-tight text-[#1A1A1A]">AURA</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#737373]">CRM · 2026</div>
              </div>
            </div>
            <p className="mt-6 text-[#4A4A4A] font-light max-w-sm" data-testid="footer-tagline">
              {t.footer.tagline}
            </p>

            {socials.length > 0 && (
              <div className="mt-8 flex items-center gap-3">
                {socials.map(({ key, Icon, url }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`social-${key}`}
                    aria-label={key}
                    className="h-10 w-10 rounded-full bg-white border border-black/5 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-all"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <FooterCol
            title={t.footer.product}
            testPrefix="product"
            links={[
              { label: t.footer.links.features, href: "#features" },
              { label: t.footer.links.pricing, href: "#pricing" },
              { label: t.footer.links.dashboard, href: "#dashboard" },
            ]}
          />
          <FooterCol
            title={t.footer.company}
            testPrefix="company"
            links={[
              { label: t.footer.links.contact, href: `mailto:${COMPANY.email}` },
              { label: t.footer.links.ai, to: "/intelligenza-artificiale" },
              { label: t.footer.links.legal_notice, to: "/note-legali" },
            ]}
          />
          <FooterCol
            title={t.footer.legal}
            testPrefix="legal"
            links={[
              { label: t.footer.links.privacy, to: "/privacy" },
              { label: t.footer.links.terms, to: "/termini" },
              { label: t.footer.links.cookies, to: "/cookie-policy" },
              {
                label: t.footer.links.cookie_prefs,
                onClick: () => {
                  revokeConsent();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                },
              },
            ]}
          />
        </div>

        {/* Dati identificativi — art. 7 D.Lgs 70/2003.
            Ogni campo è condizionale: quelli non applicabili a un'impresa
            individuale (capitale sociale) o non ancora noti restano fuori
            invece di comparire vuoti. Vedi missingLegalFields() in company.js. */}
        <div
          className="mt-12 pt-8 border-t border-black/5 text-xs text-[#737373] font-light leading-relaxed"
          data-testid="footer-legal-info"
        >
          <span className="text-[#4A4A4A]">{displayLegalName()}</span>
          {COMPANY.legalForm ? ` — ${COMPANY.legalForm.toLowerCase()}` : ""}
          {address ? ` · Sede legale ${address}` : ""}
          {COMPANY.vat ? ` · P.IVA ${COMPANY.vat}` : ""}
          {COMPANY.rea ? ` · REA ${COMPANY.rea}` : ""}
          {COMPANY.shareCapital ? ` · Capitale sociale ${COMPANY.shareCapital}` : ""} ·{" "}
          <a href={`mailto:${COMPANY.email}`} className="underline underline-offset-2 hover:text-[#1A1A1A]">
            {COMPANY.email}
          </a>
          {COMPANY.phone ? ` · ${COMPANY.phone}` : ""}
          {COMPANY.pec ? ` · PEC ${COMPANY.pec}` : ""}
          <div className="mt-2">
            {COMPANY.brand} è un prodotto di{" "}
            <a
              href={COMPANY.ownerUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="underline underline-offset-2 hover:text-[#1A1A1A]"
            >
              {COMPANY.owner}
            </a>
            .
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-xs text-[#737373]" data-testid="footer-copyright">
          <span>{t.footer.copyright}</span>
          <OdyneCredit />
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links, testPrefix }) => (
  <div className="md:col-span-2" data-testid={`footer-col-${testPrefix}`}>
    <div className="text-xs tracking-[0.3em] uppercase text-[#1A1A1A] mb-5">{title}</div>
    <ul className="space-y-3">
      {links.map((l, i) => {
        const testId = `footer-link-${testPrefix}-${i}`;
        const cls =
          "text-sm text-[#4A4A4A] hover:text-[#1A1A1A] transition-colors font-light text-left";
        return (
          <li key={i}>
            {l.to ? (
              <Link to={l.to} data-testid={testId} className={cls}>
                {l.label}
              </Link>
            ) : l.onClick ? (
              <button type="button" onClick={l.onClick} data-testid={testId} className={cls}>
                {l.label}
              </button>
            ) : (
              <a href={l.href} data-testid={testId} className={cls}>
                {l.label}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);
